/**
 * What: Defines and normalizes the cross-runtime TestFeed connection readiness contract.
 * Transport & data-flow: Shared by Shopify Worker readiness parsing and browser bootstrap parsing.
 * Algorithm: READY is downgraded unless shop, organization, and connection identifiers are all present.
 */
import { shopifyConnectionCleanupSchema, type ShopifyConnectionCleanup } from '@testfeed/integration-contracts'
import type { StudyBuilderGuidance } from '@testfeed/shopify-contracts'
import type { PageErrorName } from './page-error-contract'
/**
 * Builder load failures use the shared page lifecycle names. Keep the historical exports as aliases,
 * so Worker wording and browser classification cannot grow independent name lists.
 */
export { PAGE_ERROR_NAMES as BUILDER_LOAD_ERROR_NAMES } from './page-error-contract'
export type { PageErrorName as BuilderLoadErrorName } from './page-error-contract'

/** The finished words a failed Study Builder load renders. `canRetry` is the Worker's verdict on whether a retry can help. */
export type BuilderLoadErrorWords = { title: string; body: string; canRetry: boolean }

/**
 * The server-owned words for every load state, resolved by the Worker from product-language and carried on the
 * connection-status payload so they exist before the builder mounts. This is the only source for a load that fails
 * before (or without) a Worker response — a browser-thrown integrity/scope error, a retired link, or a network drop.
 */
export type BuilderLoadErrorDictionary = Record<PageErrorName, BuilderLoadErrorWords>

export type TestFeedConnectionStatusCode =
  | 'READY'
  | 'TESTFEED_INTERNAL_API_SECRET_MISSING'
  | 'SHOPIFY_CONNECTION_NOT_FOUND'
  | 'SHOPIFY_CONNECTION_DISCONNECTED'
  | 'SHOPIFY_CONNECTION_UNINSTALLED'
  | 'SHOPIFY_COLLECTION_HELD'
  | 'SHOPIFY_CONNECTION_SELECTION_REQUIRED'
  | 'DEFAULT_PROJECT_NOT_CONFIGURED'
  | 'TESTFEED_CONNECTION_STATUS_UNAVAILABLE'

export type TestFeedConnectionStatus = {
  builderGuidance?: StudyBuilderGuidance | null
  /** The Worker-resolved load-error words for the Study Builder; present on a connected (READY) status. */
  builderLoadError?: BuilderLoadErrorDictionary | null
  ready: boolean
  code: TestFeedConnectionStatusCode
  message: string
  connectionId?: string
  defaultProjectId?: string
  connectionStatus?: string
  externalAccountId?: string
  orgId?: string
  /** Web-owned activation identity; fencing compares it exactly, including on disabled retries. */
  connectionActivatedAt?: string
  /** Authoritative cleanup readback for the selected connection; absent/null never means completed. */
  cleanup?: ShopifyConnectionCleanup | null
  /** Privacy-owned collection period; absence is legacy, never today's implicit period. */
  collectionId?: string
  defaultModelId?: string
  defaultSampleSize?: number
  storeIntelligenceModelId?: string
  opportunityQuestionModelId?: string
  opportunityRewriteModelId?: string
  fieldClassifierModelId?: string
}

/** Checks that an unknown payload has the transport-level readiness fields. */
export function isTestFeedConnectionStatus(value: unknown): value is TestFeedConnectionStatus {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.ready === 'boolean' && typeof candidate.code === 'string' && typeof candidate.message === 'string'
  )
}

/** Downgrades incomplete READY payloads before either runtime can enable connected behavior. */
export function normalizeTestFeedConnectionStatus(status: TestFeedConnectionStatus): TestFeedConnectionStatus {
  if (
    status.cleanup !== undefined &&
    status.cleanup !== null &&
    !shopifyConnectionCleanupSchema.safeParse(status.cleanup).success
  ) {
    return {
      ready: false,
      code: 'TESTFEED_CONNECTION_STATUS_UNAVAILABLE',
      message: 'Invalid TestFeed connection cleanup status received from server.',
    }
  }
  if (
    (status.connectionActivatedAt !== undefined &&
      (typeof status.connectionActivatedAt !== 'string' ||
        !Number.isFinite(Date.parse(status.connectionActivatedAt)))) ||
    (status.collectionId !== undefined &&
      (typeof status.collectionId !== 'string' ||
        !/^[a-f0-9]{64}$/.test(status.collectionId) ||
        !status.connectionActivatedAt))
  ) {
    return {
      ready: false,
      code: 'TESTFEED_CONNECTION_STATUS_UNAVAILABLE',
      message: 'Invalid TestFeed connection collection authority received from server.',
    }
  }
  if (status.ready && status.code === 'READY') {
    const shop = status.externalAccountId?.trim()
    const org = status.orgId?.trim()
    const connection = status.connectionId?.trim()
    if (!shop || !org || !connection) {
      return {
        ready: false,
        code: 'TESTFEED_CONNECTION_STATUS_UNAVAILABLE',
        message: 'Incomplete TestFeed connection configuration received from server.',
      }
    }
  }
  return status
}
